/** Email sign-up form with password confirmation on the login page. */
import { useId } from 'react';
import { useT } from '@/i18n/useT';
import LoginPasswordField from '@/features/auth/LoginPasswordField';
import PasswordStrengthIndicator from '@/features/auth/PasswordStrengthIndicator';

const LoginSignUpForm = ({
  email,
  password,
  passwordConfirm,
  fieldErrors,
  showPassword,
  showConfirmPassword,
  loading,
  onEmailChange,
  onPasswordChange,
  onPasswordConfirmChange,
  onEmailBlur,
  onPasswordBlur,
  onPasswordConfirmBlur,
  onToggleShowPassword,
  onToggleShowConfirmPassword,
  onSubmit,
}) => {
  const t = useT();
  const emailErrorId = useId();
  const hasEmailError = Boolean(fieldErrors.email);

  return (
    <form className="login-form" onSubmit={onSubmit} noValidate>
      <div className="login-field">
        <label htmlFor="login-email">{t('login.emailLabel', 'Email')}</label>
        <input
          id="login-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={onEmailChange}
          onBlur={onEmailBlur}
          aria-invalid={hasEmailError ? 'true' : 'false'}
          aria-describedby={hasEmailError ? emailErrorId : undefined}
        />
        {hasEmailError ? (
          <p id={emailErrorId} className="login-field-error" role="alert">
            {t(fieldErrors.email, fieldErrors.email)}
          </p>
        ) : null}
      </div>
      <LoginPasswordField
        id="login-password"
        label={t('login.passwordLabel', 'Password')}
        value={password}
        onChange={onPasswordChange}
        onBlur={onPasswordBlur}
        autoComplete="new-password"
        errorKey={fieldErrors.password}
        showPassword={showPassword}
        onToggleShow={onToggleShowPassword}
      />
      <PasswordStrengthIndicator password={password} />
      <LoginPasswordField
        id="login-password-confirm"
        label={t('login.passwordConfirmLabel', 'Confirm password')}
        value={passwordConfirm}
        onChange={onPasswordConfirmChange}
        onBlur={onPasswordConfirmBlur}
        autoComplete="new-password"
        errorKey={fieldErrors.passwordConfirm}
        showPassword={showConfirmPassword}
        onToggleShow={onToggleShowConfirmPassword}
      />
      <button
        type="submit"
        className="login-submit-button"
        disabled={loading}
        data-testid="login-submit"
      >
        {loading
          ? t('login.submitting', 'Creating account…')
          : t('login.tabSignUp', 'Create account')}
      </button>
    </form>
  );
};

export default LoginSignUpForm;
